/**
 * Цвет метки байка.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * КАРТА СКАУТА — ВИД РАБОТЫ (`markerKind`), ПАЛИТРОЙ ОБРАЗЦА (10.10)
 * ─────────────────────────────────────────────────────────────────────────
 * Операционный менеджер: по палитре bo «не видно, где какой велик», а
 * образец (свободен / разряжен / сломан / за зоной) читается сразу. Поэтому
 * кружок на карте красится по тому, что с великом делать, а не по статусу.
 *
 * ⚠ `faulty` — НЕ ВСЕГДА «сломан». На 10.10 из 25 `faulty` в проде сломан
 * один («Спущено колесо»): остальные — удержания staff с причиной
 * «Разряжен» (12), «Перевозка, передислокация» (9), «Вне зоны» (3).
 * Красным по статусу карта стала бы красной от исправных великов, поэтому
 * у `faulty` цвет решает причина (`faultReason`) — тем же правилом
 * удержания, что у пула (`isRouteHold`).
 *
 * Палитра bo (`STATUS_COLOR`) осталась у чипа статуса в карточке и у
 * мини-карты карточки: там вопрос «какой статус», как у диспетчера.
 *
 * `open_no_ride` из bo здесь нет: это не статус, а вывод bo из живых аренд,
 * которых скауту не отдают.
 *
 * Номер на метке — `lib/bike-number.ts`, `shortLabel`: скаут ищет глазами
 * номер, а не процент.
 */
import type { BikeStatus } from './describe-bike'
import { isRouteHold } from './route-pick'

export type MarkerKind = 'free' | 'low' | 'broken' | 'out' | 'move' | 'busy'

/** Порядок легенды. */
export const MARKER_KINDS: readonly MarkerKind[] = ['free', 'low', 'broken', 'out', 'move', 'busy']

export const KIND_COLOR: Record<MarkerKind, string> = {
  free: '#22a559',
  low: '#e8a317',
  broken: '#d63b3b',
  out: '#7b5bd6',
  // Не голубой: голубые уже «вы здесь» и хабы.
  move: '#2f6fde',
  busy: '#8e8e93',
}

export const KIND_LABEL: Record<MarkerKind, string> = {
  free: 'свободен',
  low: 'разряжен',
  broken: 'сломан',
  out: 'за зоной',
  move: 'перевозка',
  busy: 'занят',
}

/** Номер на кружке — как в образце: тёмный на жёлтом, белый на остальных. */
export function kindTextColor(kind: MarkerKind): string {
  return kind === 'low' ? '#1d1d1f' : '#ffffff'
}

/** «Только забрать»: работа водителя. Свободные и занятые прячутся. */
export const PICKUP_KINDS: ReadonlySet<MarkerKind> = new Set<MarkerKind>([
  'low',
  'broken',
  'out',
  'move',
])

/** В аренде, в брони, в чужом или своём пуле, в машине — велик уже кто-то держит. */
const BUSY = new Set(['reserved', 'in_ride', 'in_pool', 'in_transit'])

/**
 * Что делать с великом — один цвет на кружок.
 *
 * Приоритет — срочность `describeBike` (`URGENCY`): сломан → разряжен → за
 * зоной → свободен. Разряженный за зоной — жёлтый: его везут на базу
 * заряжать (`poolDestination`), а не в хаб. Чей-то (аренда, пул, машина) —
 * серый при любом заряде: это не работа скаута.
 *
 * «Разряжен» включает неизвестный заряд и «не на связи»: молчащий велик чаще
 * всего сел (так же решают чип «Разряженные» и админка), и зелёным он
 * пропал бы из «Только забрать».
 *
 * «За зоной» — причина `out_of_zone` у `describeBike`: вне разрешённых зон
 * (`isOutsideRidingArea`) плюс запрещённая зона и серверный `problem`.
 * Велик в запрещённом дворе — тоже работа, зелёным его быть не должно.
 *
 * `at_base` на карте не рисуется (домик базы); в списке базы он серый.
 */
export function markerKind(
  bike: { status: string; faultReason?: string },
  status: Pick<BikeStatus, 'reasons'>,
): MarkerKind {
  if (BUSY.has(bike.status)) return 'busy'
  if (bike.status === 'servicing' || bike.status === 'lost') return 'broken'
  if (bike.status === 'faulty') {
    const reason = (bike.faultReason ?? '').trim().toLowerCase()
    if (isRouteHold(bike)) return reason.startsWith('разря') ? 'low' : 'move'
    return reason.includes('зон') ? 'out' : 'broken'
  }
  const codes = new Set(status.reasons.map((r) => r.code))
  if (codes.has('low_battery') || codes.has('unknown_battery') || codes.has('offline')) return 'low'
  if (codes.has('out_of_zone')) return 'out'
  return bike.status === 'available' ? 'free' : 'busy'
}

/**
 * Палитра bo — копия `STATUS_MAP_COLOR` из sharing-admin
 * (`src/pages/fleet/components/fleet-ui.tsx`); правится там — правится и здесь.
 */
export const STATUS_COLOR: Record<string, string> = {
  available: '#3dd68c',
  reserved: '#4c9aff',
  in_ride: '#a78bfa',
  faulty: '#f0a22e',
  servicing: '#f0a22e',
  in_transit: '#2dd4bf',
  in_pool: '#38bdf8',
  at_base: '#a3b0b8',
  lost: '#e5484d',
  written_off: '#46545e',
}

/** Фолбэк bo (`statusColor`): незнакомый статус — как списанный, не невидимый. */
const UNKNOWN_STATUS_COLOR = '#46545e'

export function statusColor(status: string): string {
  return STATUS_COLOR[status] ?? UNKNOWN_STATUS_COLOR
}

/** Режим «Пул»: байк выбрать нельзя — цвет состояния уступает «недоступен». */
export const DIMMED_COLOR = '#64748b'

/** Номер на светлой заливке. */
export const LABEL_COLOR = '#0b0d11'
const LABEL_ON_DARK = '#ffffff'

/**
 * Цвет номера под заливку: тот из двух, у которого контраст выше (WCAG).
 *
 * ⚠ НЕ ОДИН ТЁМНЫЙ НА ВСЁ. Палитра bo не вся светлая: на «списан» #46545e
 * тёмный номер почти не виден, а номер — главное, что на метке читают.
 */
export function labelColorOn(background: string): string {
  const l = luminance(background)
  const onDark = (luminance(LABEL_ON_DARK) + 0.05) / (l + 0.05)
  const onLight = (l + 0.05) / (luminance(LABEL_COLOR) + 0.05)
  return onDark > onLight ? LABEL_ON_DARK : LABEL_COLOR
}

function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Отметка «Забираю» — обводка и заливка метки.
 *
 * ⚠ НЕ ИЗ `STATUS_COLOR`, И ЭТО НАМЕРЕННО. Статус отвечает на вопрос «что с
 * байком», отметка — на вопрос «беру ли я его». Взять оранжевый из
 * «сломан» значило бы, что отмеченный исправный байк выглядит сломанным.
 */
export const PICKUP_COLOR = '#fb923c'
